import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, Plus, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import TaskRow, { PRIORITY_META } from "../components/TaskRow";
import TaskForm from "../components/TaskForm";
import Tip from "../components/Tip";

const GROUP_KEY = "pxos.grouping.deadlines";

export default function Deadlines() {
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filterArea, setFilterArea] = useState("");
  const [group, setGroup] = useState(() => { try { return localStorage.getItem(GROUP_KEY) || "priority"; } catch { return "priority"; } });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => { try { localStorage.setItem(GROUP_KEY, group); } catch {} }, [group]);

  const load = async () => {
    setLoading(true); setLoadError(null);
    try {
      const params = {};
      if (filterArea) params.area_id = filterArea;
      const [d, a] = await Promise.all([api.deadlines(params), api.listAreas()]);
      setItems(Array.isArray(d) ? d : []);
      setAreas(a || []);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Errore sconosciuto";
      setLoadError({ status, detail: typeof detail === "string" ? detail : JSON.stringify(detail) });
      toast.error(`Errore caricamento scadenze${status ? ` (HTTP ${status})` : ""}`);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterArea]);

  const areaById = useMemo(() => Object.fromEntries(areas.map((a) => [a.id, a])), [areas]);
  const complete = async (t) => { try { await api.completeTask(t.id); toast.success("✓ Fatto"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore completamento"); } };
  const trash = async (t) => {
    if (!confirm("Sposta nel cestino?")) return;
    try { await api.trashTask(t.id); toast.success("Spostato nel cestino"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore spostamento cestino"); }
  };
  const archive = async (t) => {
    try { await api.archiveTask(t.id); toast.success("Archiviato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore archiviazione"); }
  };

  const grouped = useMemo(() => {
    const groups = {};
    for (const t of items) {
      let key, label, order;
      if (group === "area") {
        key = t.area_id || "no-area";
        label = t.area_id ? (areaById[t.area_id]?.name || "?") : "Senza area";
        order = t.area_id ? (areaById[t.area_id]?.sort_order ?? 99) : 999;
      } else if (group === "date") {
        key = t.due_date || "no-date";
        label = key === "no-date" ? "Senza data" : new Date(key).toLocaleDateString("it-IT", { day: "2-digit", month: "long" });
        order = key;
      } else {
        key = t.priority; label = PRIORITY_META[key]?.label || key;
        order = { critical: 0, high: 1, medium: 2, low: 3 }[key] ?? 9;
      }
      groups[key] ??= { key, label, tasks: [], order };
      groups[key].tasks.push(t);
    }
    return Object.values(groups).sort((a, b) => {
      if (a.order < b.order) return -1;
      if (a.order > b.order) return 1;
      return 0;
    });
  }, [items, group, areaById]);

  const openNew = () => { setEditing(null); setFormOpen(true); };

  return (
    <div className="space-y-6" data-testid="deadlines-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Scadenzario</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Le mie scadenze</h1>
          <p className="mt-2 text-sm opacity-80 max-w-2xl">
            Tutte le scadenze e gli impegni con data, ordinati per priorità. Ogni scadenza
            mostra il countdown ("tra 30 giorni", "oggi", "scaduta da…") e i reminder pianificati.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Tip label="Crea una nuova scadenza">
            <button onClick={openNew}
                    className="pxos-btn-primary inline-flex items-center gap-1.5 text-sm"
                    data-testid="deadlines-new-btn">
              <Plus size={14}/> Nuova scadenza
            </button>
          </Tip>
          <Tip label="Scarica in PDF">
            <a href={api.reportDeadlinesPdfUrl()} target="_blank" rel="noreferrer"
               className="rounded-full border px-3 py-1.5 text-sm inline-flex items-center gap-1"
               data-testid="deadlines-pdf-btn"><FileDown size={14}/> PDF</a>
          </Tip>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <select className="pxos-input w-56" value={filterArea} onChange={(e) => setFilterArea(e.target.value)} data-testid="deadlines-filter-area">
          <option value="">Tutte le aree</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <div className="ml-auto inline-flex rounded-full border overflow-hidden" data-testid="deadlines-grouping">
          {[{ v: "priority", label: "Per priorità" }, { v: "area", label: "Per area" }, { v: "date", label: "Cronologico" }].map((g) => (
            <button key={g.v} onClick={() => setGroup(g.v)}
                    className={`px-3 py-1.5 text-xs font-semibold ${group === g.v ? "bg-white shadow-inner" : "opacity-70 hover:bg-white/70"}`}
                    style={{ color: "var(--pxos-text-dark)" }} data-testid={`deadlines-group-${g.v}`}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="pxos-card p-10 text-center opacity-70 flex items-center justify-center gap-2" data-testid="deadlines-loading">
          <Loader2 size={16} className="animate-spin" /> Caricamento scadenze…
        </div>
      ) : loadError ? (
        <div className="pxos-card p-6 border-2" style={{ borderColor: "#F5C4C4", background: "#FFF5F5" }} data-testid="deadlines-error">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} style={{ color: "#B42318" }} className="mt-0.5 shrink-0" />
            <div className="flex-1 text-sm" style={{ color: "#8A1F1F" }}>
              <div className="font-semibold">Impossibile caricare le scadenze</div>
              <div className="mt-1 opacity-80">
                {loadError.status ? `HTTP ${loadError.status}: ` : ""}{loadError.detail}
              </div>
            </div>
            <button onClick={load}
                    className="rounded-full border px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
                    style={{ borderColor: "#F5C4C4", color: "#B42318" }}
                    data-testid="deadlines-retry-btn">
              <RefreshCw size={12} /> Riprova
            </button>
          </div>
        </div>
      ) : grouped.length === 0 ? (
        <div className="pxos-card p-10 text-center opacity-70" data-testid="deadlines-empty">
          <div className="mb-3">Nessuna scadenza attiva.</div>
          <button onClick={openNew} className="pxos-btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="deadlines-empty-new-btn">
            <Plus size={14}/> Crea la prima scadenza
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <section key={g.key} className="pxos-card p-3" data-testid="deadlines-group">
              <h2 className="font-display text-base font-semibold px-2 py-1">{g.label}</h2>
              <div className="divide-y" style={{ borderColor: "var(--pxos-border)" }}>
                {g.tasks.map((t) => (
                  <TaskRow key={t.id} task={t} area={areaById[t.area_id]}
                    onComplete={complete}
                    onEdit={(x) => { setEditing(x); setFormOpen(true); }}
                    onArchive={archive} onDelete={trash}
                    showPriority={group !== "priority"} showArea={group !== "area"} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      <TaskForm
        open={formOpen}
        initial={editing}
        defaultType="deadline"
        onClose={() => setFormOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
